import React, { useState } from 'react';

import type { DocParts } from '@aline/common';
import { makeVar } from '@apollo/client';
import { DialogContent, FormControlLabel, RadioGroup, Typography } from '@mui/material';
import { AlineDialog, AlineDialogButtons, AlineRadio, RadioLabelWithCaption } from 'src/components';
import { useOnChange } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import { ExportDocumentCommentsMode } from './filterCommentsForExport';

export type PendingDocxExport = {
  docParts: DocParts;
  documentId: UUID;
  name: string;
};

export const exportDocumentDialogVar = makeVar<{
  open: boolean;
  pending: PendingDocxExport | null;
}>({ open: false, pending: null });

const useStyles = makeStyles()(theme => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  formControlLabel: {
    alignItems: 'flex-start',
    fontWeight: 700,
    margin: 0,
  },
  paper: {
    width: theme.dialogStyles.standardDialogWidth,
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  subtitle: {
    color: theme.palette.text.secondary,
    flexShrink: 0,
    fontFamily: theme.typography.fontFamily,
    fontSize: 14,
    fontWeight: 400,
    letterSpacing: 0,
  },
}));

export interface ExportDocumentDialogProps {
  onClose: () => void;
  onExport: (commentsMode: ExportDocumentCommentsMode) => void | Promise<void>;
  open: boolean;
  loading?: boolean;
}

export function ExportDocumentDialog({
  loading = false,
  onClose,
  onExport,
  open,
}: ExportDocumentDialogProps): JSX.Element {
  const { classes } = useStyles();
  const [commentsMode, setCommentsMode] = useState(ExportDocumentCommentsMode.PublicOnly);

  useOnChange(() => {
    if (open) setCommentsMode(ExportDocumentCommentsMode.PublicOnly);
  }, [open]);

  return (
    <AlineDialog
      classes={{ paperWidthMd: classes.paper }}
      disableAltClose={loading}
      loading={loading}
      open={open}
      title="Export document"
      onClose={onClose}
    >
      <DialogContent className={classes.content}>
        <Typography className={classes.subtitle} variant="body2">
          Choose which comments you want to include in the exported file.
        </Typography>
        <RadioGroup
          aria-label="comment export scope"
          className={classes.radioGroup}
          value={commentsMode}
          onChange={(_, value) => setCommentsMode(value as ExportDocumentCommentsMode)}
        >
          <FormControlLabel
            className={classes.formControlLabel}
            control={<AlineRadio size="small" />}
            label={
              <RadioLabelWithCaption
                caption="Exports only comments visible to external collaborators"
                label="Public comments only"
              />
            }
            value={ExportDocumentCommentsMode.PublicOnly}
          />
          <FormControlLabel
            className={classes.formControlLabel}
            control={<AlineRadio size="small" />}
            label={
              <RadioLabelWithCaption
                caption="Includes private and public comments"
                label="All comments"
              />
            }
            value={ExportDocumentCommentsMode.All}
          />
        </RadioGroup>
      </DialogContent>
      <AlineDialogButtons
        cancelHandler={onClose}
        loading={loading}
        submitHandler={() => onExport(commentsMode)}
        submitText="Export Document"
      />
    </AlineDialog>
  );
}
